/*
 * Reglas de presentación del Historial (buscador y expediente del vehículo).
 * Los datos vienen de /api/historial (ver CONTRATO-API.md de la tarea);
 * aquí solo se traducen para mostrarlos.
 */
import { capitalizarNombre, nombreDeModelo, textoOracion } from '@/utils/formato'

// orderservices.status: texto "0".."4"
export const ESTADOS_ORDEN = {
  0: { texto: 'Cancelada', tono: 'peligro' },
  1: { texto: 'En recepción', tono: 'marca' },
  2: { texto: 'En taller', tono: 'aviso' },
  3: { texto: 'Servicio finalizado', tono: 'marca' },
  4: { texto: 'Entregada', tono: 'exito' },
}

export function estadoDeOrden(estado) {
  return ESTADOS_ORDEN[String(estado ?? '').trim()] || { texto: 'Sin estado', tono: 'neutro' }
}

export function esCancelada(orden) {
  return String(orden?.estado ?? '').trim() === '0'
}

/*
 * Una sola regla para el nombre del auto en todo el Historial, venga en
 * partes o en la `descripcion` de la API (que ya trae la marca):
 * "NISSAN SENTRA 2019" → "Nissan Sentra 2019", "RAM 1500 2020" → "Ram 1500 2020",
 * "BMW X5 2018" → "BMW X5 2018" (códigos y siglas sin vocales se quedan).
 */
export function descripcionDeAuto(descripcion) {
  return nombreDeModelo(descripcion) || 'Auto sin modelo'
}

/* { marca: 'NISSAN', modelo: 'SENTRA', anio: 2019 } → "Nissan Sentra 2019". */
export function nombreDeAuto(auto = {}) {
  return descripcionDeAuto([auto.marca, auto.modelo, auto.anio].filter((parte) => parte || parte === 0).join(' '))
}

const TRANSMISIONES = {
  AUTOMATICO: 'automático',
  AUTOMATICA: 'automático',
  AUTOMÁTICO: 'automático',
  AUTOMÁTICA: 'automático',
  ESTANDAR: 'estándar',
  ESTÁNDAR: 'estándar',
  MANUAL: 'manual',
  CVT: 'CVT',
}

/* Color, transmisión y cilindros: "Gris plata · automático · 4 cilindros". */
export function detallesDeAuto(vehiculo = {}) {
  const color = textoOracion(vehiculo.color)
  const clave = String(vehiculo.transmision ?? '').trim().toLocaleUpperCase('es-MX')
  const transmision = clave ? TRANSMISIONES[clave] || clave.toLocaleLowerCase('es-MX') : ''
  const cilindros = Number(vehiculo.cilindros) > 0 ? `${Number(vehiculo.cilindros)} cilindros` : ''
  const partes = [color, transmision, cilindros].filter(Boolean)
  // La primera letra en mayúscula aunque falte el color
  return partes.join(' · ').replace(/^\p{L}/u, (letra) => letra.toLocaleUpperCase('es-MX'))
}

/* "LAURA MEDINA RUIZ" → "LM". */
export function iniciales(nombre) {
  const palabras = String(nombre ?? '').trim().split(/\s+/).filter((p) => /\p{L}/u.test(p))
  if (!palabras.length) return '?'
  const primera = palabras[0][0]
  const segunda = palabras.length > 1 ? palabras[1][0] : ''
  return (primera + segunda).toLocaleUpperCase('es-MX')
}

/*
 * Nombre de persona capitalizado y sin relleno: las partes sin letras ("."
 * o "-" capturados como apellido) se omiten. "JUAN ." → "Juan".
 */
export function nombreLimpio(nombre) {
  const palabras = String(nombre ?? '').trim().split(/\s+/).filter((p) => /\p{L}/u.test(p))
  return capitalizarNombre(palabras.join(' '))
}

/*
 * "JUAN RAMIREZ LOPEZ" → "J. Ramirez" (fila resumida de una visita). Con una
 * sola palabra va completa ("JUAN ." → "Juan"): nunca una inicial suelta.
 */
export function nombreCorto(nombre) {
  const palabras = nombreLimpio(nombre).split(' ').filter(Boolean)
  if (palabras.length < 2) return palabras[0] || ''
  return `${palabras[0][0]}. ${palabras[1]}`
}

/*
 * ¿Es un clic simple que la pantalla puede navegar con el router? Con Ctrl,
 * Cmd, Mayús o Alt, o con otro botón, se deja al navegador (pestaña nueva).
 */
export function esClicSimple(event) {
  if (!event) return true
  return !(event.defaultPrevented || (event.button !== undefined && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
}

/* Kilometraje → dígitos del odómetro, con ceros a la izquierda: 84310 → ['0','8','4','3','1','0']. */
export function digitosOdometro(km, minimo = 6) {
  const numero = Number(km)
  if (!Number.isFinite(numero) || numero < 0) return []
  return String(Math.round(numero)).padStart(minimo, '0').split('')
}

export const FILTROS_VISITAS = [
  { id: 'todas', texto: 'Todas' },
  { id: 'anio', texto: 'Último año' },
  { id: 'factura', texto: 'Con factura' },
  { id: 'canceladas', texto: 'Canceladas' },
]

const UN_ANIO_MS = 365 * 24 * 60 * 60 * 1000

/* Filtros del expediente, en el cliente (la API manda todas las órdenes del auto). */
export function filtrarVisitas(visitas = [], filtro = 'todas', ahora = new Date()) {
  const lista = Array.isArray(visitas) ? visitas : []
  if (filtro === 'anio') {
    const desde = new Date(ahora).getTime() - UN_ANIO_MS
    return lista.filter((v) => {
      const fecha = new Date(v.recibida).getTime()
      return Number.isFinite(fecha) && fecha >= desde
    })
  }
  if (filtro === 'factura') return lista.filter((v) => Boolean(v.factura))
  if (filtro === 'canceladas') return lista.filter(esCancelada)
  return lista
}

/*
 * Vistos recientemente: los últimos autos abiertos en esta sesión. Solo se
 * guarda lo del auto (id, descripción y placa), nunca datos del dueño, y en
 * sessionStorage: cerrar sesión lo borra (Sistema.vue hace sessionStorage.clear),
 * así el siguiente usuario de una PC compartida no lo ve. Si el almacenamiento
 * falla (modo privado, bloqueado) la lista queda vacía y la pantalla sigue igual.
 */
export const CLAVE_RECIENTES = 'rm.historial.recientes'
export const MAX_RECIENTES = 6

function almacen() {
  try {
    return window.sessionStorage || null
  } catch (error) {
    return null
  }
}

const texto = (valor) => (typeof valor === 'string' ? valor : '')

export function leerRecientes() {
  try {
    const destino = almacen()
    const lista = JSON.parse((destino && destino.getItem(CLAVE_RECIENTES)) || '[]')
    if (!Array.isArray(lista)) return []
    return lista
      .filter((auto) => auto && typeof auto === 'object' && typeof auto._id === 'string' && auto._id)
      .map((auto) => ({ _id: auto._id, descripcion: texto(auto.descripcion), placa: texto(auto.placa) }))
      .slice(0, MAX_RECIENTES)
  } catch (error) {
    return []
  }
}

export function guardarReciente(auto) {
  if (!auto || !auto._id) return leerRecientes()
  const nuevo = { _id: String(auto._id), descripcion: texto(auto.descripcion), placa: texto(auto.placa) }
  const lista = [nuevo, ...leerRecientes().filter((reciente) => reciente._id !== nuevo._id)].slice(0, MAX_RECIENTES)
  try {
    const destino = almacen()
    if (destino) destino.setItem(CLAVE_RECIENTES, JSON.stringify(lista))
  } catch (error) {
    // Sin almacenamiento: no se recuerda, pero no se interrumpe nada
  }
  return lista
}
